import classNames from 'classnames';

import BaseSideMenuItem from './base-side-menu-item';

import type { NavItem } from 'components/shared/side-menu/nav-config';

import Icons from 'assets/icons';

interface BaseExpandedSectionProps {
  title?: string;
  titleIcon?: React.ReactNode;
  visibleItems: NavItem[];
  isCollapsed: boolean;
  sectionExpanded: boolean;
  showToggle?: boolean;
  onToggle?: () => void;
  isItemActive: (item: NavItem) => boolean;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  getNotification?: (item: NavItem) => boolean;
  className?: string;
}

const BaseExpandedSection = ({
  title,
  titleIcon,
  visibleItems,
  isCollapsed,
  sectionExpanded,
  showToggle,
  onToggle,
  isItemActive,
  renderLink,
  getNotification,
  className,
}: BaseExpandedSectionProps) => (
  <div className={classNames('py-2', className)}>
    {title && (
      <div className="flex items-center justify-between px-3 mb-1">
        {showToggle ? (
          <button
            onClick={onToggle}
            className="flex items-center justify-between w-full text-gray-700 hover:text-gray-500 transition-colors py-1 font-semibold text-base"
            aria-expanded={sectionExpanded}
          >
            <span className="flex items-center gap-2">
              {titleIcon && <span aria-hidden="true">{titleIcon}</span>}
              {title}
            </span>
            <Icons.Arrow
              customClass={classNames(
                'w-4 h-4 transition-transform duration-200',
                { 'rotate-90': sectionExpanded }
              )}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="flex items-center gap-2 text-gray-700 py-1 font-semibold text-base">
            {titleIcon && <span aria-hidden="true">{titleIcon}</span>}
            {title}
          </span>
        )}
      </div>
    )}
    <div
      className={classNames(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        sectionExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <nav
        className={classNames('space-y-0.5 mt-1 overflow-hidden', {
          'pl-4 pr-2': title,
          'px-2': !title,
        })}
        aria-label={title || 'Menu section'}
      >
        {visibleItems.map((item) => (
          <BaseSideMenuItem
            key={item.id}
            item={item}
            isCollapsed={isCollapsed}
            isItemActive={isItemActive}
            renderLink={renderLink}
            hasNotification={getNotification?.(item) ?? false}
          />
        ))}
      </nav>
    </div>
  </div>
);

export default BaseExpandedSection;
